import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';
import type { BoardObject } from '../interfaces/board-object.interface.js';
import type { TextBox } from '../../features/textbox/textbox.interface.js';
import type { TextDocument } from '../../features/textbox/text-document.type.js';

/**
 * Apply a whole-textbox format in ONE undoable step: replace the TextBox's
 * `document` (with the format already pushed onto every run / block) and,
 * optionally, the matching container-default property (fontSize / color /
 * hAlign) so freshly typed text inherits it and the options-circle highlight
 * reflects the new value. Revert restores both fields atomically.
 */
export class ApplyWholeTextFormatCommand implements Command<BoardState> {
  readonly label = 'Textformat ändern';
  #prevDocument: TextDocument | undefined;
  #prevContainer: unknown;
  #captured = false;

  constructor(
    private readonly id: string,
    private readonly document: TextDocument,
    private readonly containerKey?: string,
    private readonly containerValue?: unknown,
  ) {}

  apply(state: BoardState): void {
    const obj = state.getObject(this.id);
    if (!obj || obj.type !== 'text') return;
    const tb = obj as TextBox;
    this.#prevDocument = tb.document;
    if (this.containerKey !== undefined) {
      this.#prevContainer = (tb as unknown as Record<string, unknown>)[this.containerKey];
    }
    this.#captured = true;
    state.updateObject({ ...obj, ...this.#patch(this.document, this.containerValue) } as BoardObject);
  }

  revert(state: BoardState): void {
    const obj = state.getObject(this.id);
    if (!obj || !this.#captured || this.#prevDocument === undefined) return;
    state.updateObject({ ...obj, ...this.#patch(this.#prevDocument, this.#prevContainer) } as BoardObject);
  }

  #patch(document: TextDocument, containerValue: unknown): Record<string, unknown> {
    const patch: Record<string, unknown> = { document };
    if (this.containerKey !== undefined) patch[this.containerKey] = containerValue;
    return patch;
  }
}
